import type {
  ContractSchema,
  MembershipRecord
} from '@libs/data-access/entities'
import { Alert, Flex } from '@libs/ui/ds'
import { usePlanningTranslation } from '@libs/utils/translations'
import type { ReduxState } from '@spa/redux/ReduxState'
import { useMemoSelector } from '@spa/redux/hooks'
import type { FC } from 'react'
import React from 'react'
import { connect } from 'react-redux'
import { useAccountPermissions } from 'snap-redux/modules/accounts/hooks'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'

import UsersWithoutEmailsItem from './UserWithoutEmailsItem'

type Props = {
  memberships: MembershipRecord[]
}

const UsersWithoutEmailsAlert: FC<Props> = ({ memberships }) => {
  const { t } = usePlanningTranslation()
  const { sms_enabled } = useAccountPermissions()
  const { employee_can_receive_sms_notification } =
    useMemoSelector(getCurrentAccount)

  const membershipWithoutNotification = memberships.filter(membership => {
    const haveNoEmail = !membership?.user?.get('email')
    const haveNoSMS = !membership?.sms_notification_enabled

    if (employee_can_receive_sms_notification && sms_enabled) {
      return haveNoEmail && haveNoSMS
    }
    return haveNoEmail
  })

  if (membershipWithoutNotification.length <= 0) {
    return null
  }

  return (
    <Alert
      intent='semantic.orange'
      title={t(
        'navbar.weeklySchedulePublishButton.usersWithoutEmailsTrick.user',
        { count: membershipWithoutNotification.length }
      )}
    >
      <Flex className='gap-2'>
        {membershipWithoutNotification?.map(membership => (
          <Flex key={membership.id}>
            <UsersWithoutEmailsItem membership={membership} />
          </Flex>
        ))}
      </Flex>
    </Alert>
  )
}

export default connect(
  (state: ReduxState, ownProps: { contracts?: ContractSchema[] }) => {
    const { contracts } = ownProps

    // Sometime we have some memberships without contract nor user. It break the application logic and shouldn't exist
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    const memberships = contracts
      .map(contract =>
        state.memberships.getIn(['data', contract.membership_id])
      )
      .filter(membership => membership && membership.most_relevant_contract)
      .filter(membership => membership && membership.user)
      .filter(Boolean)

    // THIS PART IS ONLY FOR THE SAKE OF V1 AND V2 USING IMMUTABLE AND NONE IMMUTABLE OBJECTS

    return { memberships }
  }
)(UsersWithoutEmailsAlert)
